import { STAFF_IDENTIFY_CLIENT_ID } from "@tovi/shared";
import { baseUrl } from "./api.ts";

/**
 * Requesting and polling an identification code (`#212`), and the link the
 * till's QR encodes for it.
 *
 * Plain `fetch` against Better Auth's own device endpoints, deliberately not
 * `api()`: their failures are RFC 8628 bodies (`{ error, error_description }`,
 * status 400) rather than the app's envelope, so `api()` would report every
 * ordinary `authorization_pending` as `INTERNAL`. Worse, `api()` signs the
 * terminal out on any 401 (`lib/api.ts`), which a device endpoint that has
 * nothing to do with the terminal's own credential must never be able to
 * trigger. Neither call below carries the terminal's bearer.
 */

export type IdentifyCode = {
  readonly deviceCode: string;
  readonly userCode: string;
  readonly expiresAt: number;
  readonly intervalMs: number;
};

export async function requestIdentifyCode(): Promise<IdentifyCode> {
  const response = await fetch(`${baseUrl()}/api/auth/device/code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ client_id: STAFF_IDENTIFY_CLIENT_ID }),
  });
  if (!response.ok) throw new Error("Requesting an identification code failed.");

  const body = (await response.json()) as {
    device_code: string;
    user_code: string;
    expires_in: number;
    interval: number;
  };
  return {
    deviceCode: body.device_code,
    userCode: body.user_code,
    expiresAt: Date.now() + body.expires_in * 1000,
    intervalMs: body.interval * 1000,
  };
}

export type PollResult =
  | { kind: "granted"; accessToken: string }
  | { kind: "pending" }
  | { kind: "slow_down" }
  | { kind: "expired" }
  | { kind: "denied" }
  | { kind: "offline" };

export async function pollIdentifyCode(deviceCode: string): Promise<PollResult> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl()}/api/auth/device/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "urn:ietf:params:oauth:grant-type:device_code",
        device_code: deviceCode,
        client_id: STAFF_IDENTIFY_CLIENT_ID,
      }),
    });
  } catch {
    return { kind: "offline" };
  }

  if (response.ok) {
    const body = (await response.json().catch(() => null)) as { access_token?: string } | null;
    if (!body?.access_token) return { kind: "offline" };
    return { kind: "granted", accessToken: body.access_token };
  }

  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  switch (body?.error) {
    case "authorization_pending":
      return { kind: "pending" };
    case "slow_down":
      return { kind: "slow_down" };
    // The code is gone either way — expired on the server, or already
    // consumed by a claim that never finished.
    case "expired_token":
    case "invalid_grant":
      return { kind: "expired" };
    case "access_denied":
      return { kind: "denied" };
    default:
      return { kind: "offline" };
  }
}

/**
 * What the QR encodes, and the shape a scanned code must match (`#212`): the
 * staff app's `/approve` screen with `user_code` and nothing else — RFC 8628's
 * `verification_uri_complete`, built here rather than read off the server's
 * own response, since that value is one setting shared with the till-pairing
 * flow and points nowhere the staff app answers.
 */
export function identifyLink(staffUrl: string, userCode: string): string {
  return `${staffUrl}/approve?user_code=${encodeURIComponent(userCode)}`;
}
